import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Divider,
  Button,
  Snackbar,
  TextField,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  AccountCircle,
  Logout,
  Close as CloseIcon,
  DoneAll as DoneAllIcon,
  CheckCircleOutline as CheckIcon,
  Chat as ChatIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { useAdmin } from '../context/AdminContext';
import adminChatService from '../services/adminChatService';
import { db, serverTimestamp } from '../firebase/firebaseConfig';
import { ROLE_LABELS } from '../constants/rbac';
import { uploadProfileImage } from '../utils/profileImageUpload';

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Navbar = () => {
  const navigate = useNavigate();
  const { user, signOut, drivers, inspections, hasAccess, updateDashboardUser } = useAdmin();
  const [anchorNotif, setAnchorNotif] = React.useState(null);
  const [anchorProfile, setAnchorProfile] = React.useState(null);
  const [openProfileDialog, setOpenProfileDialog] = React.useState(false);
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [uploadingProfileImage, setUploadingProfileImage] = React.useState(false);
  const [profileForm, setProfileForm] = React.useState({
    name: '',
    email: '',
    photoURL: '',
  });
  const [profileSnackbar, setProfileSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [totalUnreadChats, setTotalUnreadChats] = React.useState(0);
  const [adminNotifications, setAdminNotifications] = React.useState([]);
  const previousUnreadChatsRef = React.useRef(0);

  const canAccessChatSupport = hasAccess('chatSupport');
  const canViewTaskStatusNotifications = hasAccess('tasks') || hasAccess('inspection');
  const canViewNotifications =
    hasAccess('drivers') || hasAccess('inspection') || canViewTaskStatusNotifications;

  const [readNotifications, setReadNotifications] = React.useState(() => {
    const stored = localStorage.getItem('readNotifications');
    if (!stored) {
      return {};
    }
    const parsed = JSON.parse(stored);
    const threeHoursAgo = Date.now() - 3 * 60 * 60 * 1000;
    return Object.entries(parsed)
      .filter(([_, timestamp]) => timestamp > threeHoursAgo)
      .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
  });

  const [dismissedNotifications, setDismissedNotifications] = React.useState(() => {
    const stored = localStorage.getItem('dismissedNotifications');
    if (!stored) {
      return {};
    }
    const parsed = JSON.parse(stored);
    const threeHoursAgo = Date.now() - 3 * 60 * 60 * 1000;
    return Object.entries(parsed)
      .filter(([_, timestamp]) => timestamp > threeHoursAgo)
      .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
  });

  React.useEffect(() => {
    if (!canAccessChatSupport) {
      previousUnreadChatsRef.current = 0;
      setTotalUnreadChats(0);
      return undefined;
    }

    return adminChatService.subscribeToConversations((conversations) => {
      const totalUnread = conversations.reduce((sum, conversation) => sum + (conversation.unreadByAdmin || 0), 0);

      if (totalUnread > previousUnreadChatsRef.current) {
        try {
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZRA0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsIHm7A7+OaRQ0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsIHm7A7+OaRQ0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsIHm7A7+OaRQ0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsIHm7A7+OaRQ0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsIHm7A7+OaRQ0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsIHm7A7+OaRQ0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsIHm7A7+OaRQ0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsIHm7A7+OaRQ0PVqzn77BdGAg+lt7xv3IdBSuByvLaizsI');
          audio.play().catch(() => {});
        } catch (error) {
          console.log('Audio error:', error);
        }
      }

      previousUnreadChatsRef.current = totalUnread;
      setTotalUnreadChats(totalUnread);
    });
  }, [canAccessChatSupport]);

  React.useEffect(() => {
    if (!canViewTaskStatusNotifications) {
      setAdminNotifications([]);
      return undefined;
    }

    const adminNotificationsQuery = query(
      collection(db, 'adminNotifications'),
      orderBy('createdAt', 'desc'),
      limit(30)
    );

    return onSnapshot(
      adminNotificationsQuery,
      (snapshot) => {
        const notifications = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((notification) => notification.read !== true);
        setAdminNotifications(notifications);
      },
      (error) => {
        console.error('Failed to listen to admin notifications:', error);
        setAdminNotifications([]);
      }
    );
  }, [canViewTaskStatusNotifications]);

  const handleLogout = async () => {
    await signOut();
    setOpenProfileDialog(false);
    setAnchorProfile(null);
    navigate('/login');
  };

  const showProfileSnackbar = (message, severity = 'success') => {
    setProfileSnackbar({ open: true, message, severity });
  };

  const handleCloseProfileSnackbar = () => {
    setProfileSnackbar((currentSnackbar) => ({ ...currentSnackbar, open: false }));
  };

  const handleOpenProfileMenu = (event) => {
    setAnchorProfile(event.currentTarget);
  };

  const handleCloseProfileMenu = () => {
    setAnchorProfile(null);
  };

  const handleOpenProfileDialog = () => {
    setProfileForm({
      name: user?.name || '',
      email: user?.email || '',
      photoURL: user?.photoURL || '',
    });
    setOpenProfileDialog(true);
  };

  const handleOpenProfileFromMenu = () => {
    handleCloseProfileMenu();
    handleOpenProfileDialog();
  };

  const handleLogoutFromMenu = async () => {
    handleCloseProfileMenu();
    await handleLogout();
  };

  const handleCloseProfileDialog = () => {
    if (savingProfile || uploadingProfileImage) {
      return;
    }

    setOpenProfileDialog(false);
  };

  const handleProfileFieldChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((currentForm) => ({ ...currentForm, [name]: value }));
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !user?.uid) {
      return;
    }

    try {
      setUploadingProfileImage(true);
      const imageUrl = await uploadProfileImage({
        file,
        folder: 'staffProfiles',
        entityId: user.uid,
      });

      setProfileForm((currentForm) => ({ ...currentForm, photoURL: imageUrl }));
      showProfileSnackbar('Profile image uploaded successfully.');
    } catch (error) {
      showProfileSnackbar(error.message || 'Failed to upload profile image.', 'error');
    } finally {
      setUploadingProfileImage(false);
      event.target.value = '';
    }
  };

  const handleSaveProfile = async () => {
    if (!user?.uid) {
      return;
    }

    const nextName = profileForm.name.trim();
    const nextEmail = profileForm.email.trim();
    const nextPhotoURL = profileForm.photoURL.trim();

    if (!nextName || !nextEmail) {
      showProfileSnackbar('Name and email are required.', 'error');
      return;
    }

    if (!isValidEmail(nextEmail)) {
      showProfileSnackbar('Please enter a valid email address.', 'error');
      return;
    }

    const updates = {};
    if (nextName !== (user.name || '')) {
      updates.name = nextName;
    }
    if (nextEmail !== (user.email || '')) {
      updates.email = nextEmail;
    }
    if (nextPhotoURL !== (user.photoURL || '')) {
      updates.photoURL = nextPhotoURL;
    }

    if (Object.keys(updates).length === 0) {
      setOpenProfileDialog(false);
      return;
    }

    setSavingProfile(true);
    const result = await updateDashboardUser(user.uid, updates);
    setSavingProfile(false);

    if (!result.success) {
      showProfileSnackbar(result.error || 'Failed to update profile.', 'error');
      return;
    }

    setOpenProfileDialog(false);
    showProfileSnackbar('Profile updated successfully.');
  };

  const isNotificationRead = (notificationId) => {
    if (!readNotifications[notificationId]) {
      return false;
    }
    return readNotifications[notificationId] > Date.now() - 3 * 60 * 60 * 1000;
  };

  const isNotificationDismissed = (notificationId) => {
    if (!dismissedNotifications[notificationId]) {
      return false;
    }
    return dismissedNotifications[notificationId] > Date.now() - 3 * 60 * 60 * 1000;
  };

  const getActiveDriverNotifications = () =>
    drivers?.filter((driver) => driver.status === 'pending' && !isNotificationDismissed(`driver-${driver.id}`)) || [];

  const getActiveInspectionNotifications = () =>
    inspections?.filter(
      (inspection) => inspection.status === 'pending' && !isNotificationDismissed(`inspection-${inspection.id}`)
    ) || [];

  const getActiveAdminNotifications = () => adminNotifications || [];

  const getUnreadCount = () => {
    const driverCount =
      drivers?.filter(
        (driver) =>
          driver.status === 'pending' &&
          !isNotificationRead(`driver-${driver.id}`) &&
          !isNotificationDismissed(`driver-${driver.id}`)
      ).length || 0;
    const inspectionCount =
      inspections?.filter(
        (inspection) =>
          inspection.status === 'pending' &&
          !isNotificationRead(`inspection-${inspection.id}`) &&
          !isNotificationDismissed(`inspection-${inspection.id}`)
      ).length || 0;
    const adminCount = getActiveAdminNotifications().length;

    return driverCount + inspectionCount + adminCount;
  };

  const markAdminNotificationAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'adminNotifications', notificationId), {
        read: true,
        readAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Failed to mark admin notification as read:', error);
    }
  };

  const handleMarkAsRead = (notificationId, event) => {
    event.stopPropagation();
    const updatedRead = { ...readNotifications, [notificationId]: Date.now() };
    setReadNotifications(updatedRead);
    localStorage.setItem('readNotifications', JSON.stringify(updatedRead));
  };

  const handleDeleteNotification = (notificationId, event) => {
    event.stopPropagation();
    const updatedDismissed = { ...dismissedNotifications, [notificationId]: Date.now() };
    setDismissedNotifications(updatedDismissed);
    localStorage.setItem('dismissedNotifications', JSON.stringify(updatedDismissed));
  };

  const handleMarkAllAsRead = async () => {
    const allDriverIds = getActiveDriverNotifications().map((driver) => `driver-${driver.id}`);
    const allInspectionIds = getActiveInspectionNotifications().map((inspection) => `inspection-${inspection.id}`);
    const updatedRead = { ...readNotifications };
    const timestamp = Date.now();

    [...allDriverIds, ...allInspectionIds].forEach((notificationId) => {
      updatedRead[notificationId] = timestamp;
    });

    setReadNotifications(updatedRead);
    localStorage.setItem('readNotifications', JSON.stringify(updatedRead));

    const adminUnread = getActiveAdminNotifications();
    if (adminUnread.length > 0) {
      await Promise.all(adminUnread.map((notification) => markAdminNotificationAsRead(notification.id)));
    }
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: 'white',
        color: '#000000',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        borderBottom: '1px solid #e0e0e0',
      }}
    >
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#000000', fontWeight: 600 }}>
          Admin Panel
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {canViewNotifications && (
            <IconButton
              size="large"
              sx={{ color: '#000000' }}
              onClick={(event) => setAnchorNotif(event.currentTarget)}
              aria-controls={Boolean(anchorNotif) ? 'notif-menu' : undefined}
              aria-haspopup="true"
            >
              <Badge badgeContent={getUnreadCount()} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}

          {canAccessChatSupport && (
            <IconButton size="large" sx={{ color: '#000000' }} onClick={() => navigate('/chat-support')}>
              <Badge badgeContent={totalUnreadChats} color="error">
                <ChatIcon />
              </Badge>
            </IconButton>
          )}

          <IconButton
            size="large"
            onClick={handleOpenProfileMenu}
            sx={{ color: '#000000' }}
            aria-controls={Boolean(anchorProfile) ? 'profile-menu' : undefined}
            aria-haspopup="true"
          >
            {user?.photoURL ? (
              <Avatar src={user.photoURL} sx={{ width: 32, height: 32 }} />
            ) : (
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#DD2424' }}>
                <AccountCircle />
              </Avatar>
            )}
          </IconButton>

          <Menu
            id="profile-menu"
            anchorEl={anchorProfile}
            open={Boolean(anchorProfile)}
            onClose={handleCloseProfileMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleOpenProfileFromMenu}>
              <AccountCircle sx={{ mr: 1, fontSize: 20 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogoutFromMenu}>
              <Logout sx={{ mr: 1, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>

          <Dialog open={openProfileDialog} onClose={handleCloseProfileDialog} maxWidth="sm" fullWidth>
            <DialogTitle>My Profile</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={profileForm.photoURL || ''} sx={{ width: 72, height: 72, bgcolor: '#f5f5f5' }}>
                    {(profileForm.name || profileForm.email || 'U').slice(0, 2).toUpperCase()}
                  </Avatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {user?.name || user?.email || 'Dashboard User'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ROLE_LABELS[user?.role] || 'Dashboard User'}
                    </Typography>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={<UploadIcon />}
                      disabled={uploadingProfileImage || savingProfile}
                    >
                      {uploadingProfileImage ? 'Uploading...' : 'Upload Image'}
                      <input hidden type="file" accept="image/*" onChange={handleProfileImageUpload} />
                    </Button>
                  </Box>
                </Box>

                {(user?.role === 'manager' || user?.role === 'dispatch') && (
                  <Typography variant="caption" color="text.secondary">
                    Staff profile edits are restricted to one change every 48 hours.
                  </Typography>
                )}

                <TextField
                  label="Full Name"
                  name="name"
                  value={profileForm.name}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  disabled={savingProfile}
                />
                <TextField
                  label="Email"
                  name="email"
                  value={profileForm.email}
                  onChange={handleProfileFieldChange}
                  fullWidth
                  disabled={savingProfile}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={handleCloseProfileDialog} disabled={savingProfile || uploadingProfileImage}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveProfile}
                disabled={savingProfile || uploadingProfileImage}
              >
                {savingProfile ? 'Saving...' : 'Save Profile'}
              </Button>
            </DialogActions>
          </Dialog>

          <Menu
            id="notif-menu"
            anchorEl={anchorNotif}
            open={Boolean(anchorNotif)}
            onClose={() => setAnchorNotif(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { width: 400, maxHeight: 500 } }}
          >
            <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Notifications
              </Typography>
              {getUnreadCount() > 0 && (
                <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAllAsRead} sx={{ fontSize: '0.75rem' }}>
                  Mark all read
                </Button>
              )}
            </Box>
            <Divider />

            {getActiveDriverNotifications().length === 0 &&
              getActiveInspectionNotifications().length === 0 &&
              getActiveAdminNotifications().length === 0 && (
              <MenuItem onClick={() => setAnchorNotif(null)}>No new notifications</MenuItem>
            )}

            {getActiveDriverNotifications().map((driver) => {
              const notificationId = `driver-${driver.id}`;
              const isRead = isNotificationRead(notificationId);

              return (
                <MenuItem
                  key={driver.id}
                  onClick={() => {
                    setAnchorNotif(null);
                    navigate(`/driver/${driver.id}`);
                  }}
                  sx={{
                    backgroundColor: isRead ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                    '&:hover': {
                      backgroundColor: isRead ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.12)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: isRead ? 400 : 600 }}>{driver.name || driver.email || 'New Driver'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Registered - {driver.createdAt?.toDate ? new Date(driver.createdAt.toDate()).toLocaleString() : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {!isRead && (
                        <Tooltip title="Mark as read">
                          <IconButton size="small" onClick={(event) => handleMarkAsRead(notificationId, event)} sx={{ p: 0.5 }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Dismiss">
                        <IconButton size="small" onClick={(event) => handleDeleteNotification(notificationId, event)} sx={{ p: 0.5 }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}

            {getActiveInspectionNotifications().map((inspection) => {
              const notificationId = `inspection-${inspection.id}`;
              const isRead = isNotificationRead(notificationId);

              return (
                <MenuItem
                  key={inspection.id}
                  onClick={() => {
                    setAnchorNotif(null);
                    navigate('/vehicle-inspection', { state: { openInspectionId: inspection.id } });
                  }}
                  sx={{
                    backgroundColor: isRead ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                    '&:hover': {
                      backgroundColor: isRead ? 'rgba(0, 0, 0, 0.04)' : 'rgba(25, 118, 210, 0.12)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: isRead ? 400 : 600 }}>
                        {inspection.driverName || 'Driver'} - Inspection
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pending Review -{' '}
                        {inspection.createdAt?.toDate ? new Date(inspection.createdAt.toDate()).toLocaleString() : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {!isRead && (
                        <Tooltip title="Mark as read">
                          <IconButton size="small" onClick={(event) => handleMarkAsRead(notificationId, event)} sx={{ p: 0.5 }}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Dismiss">
                        <IconButton size="small" onClick={(event) => handleDeleteNotification(notificationId, event)} sx={{ p: 0.5 }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </MenuItem>
              );
            })}

            {getActiveAdminNotifications().map((notification) => (
              <MenuItem
                key={notification.id}
                onClick={async () => {
                  setAnchorNotif(null);
                  await markAdminNotificationAsRead(notification.id);

                  if (notification.taskId && hasAccess('tasks')) {
                    navigate('/tasks');
                    return;
                  }

                  if (notification.inspectionId && hasAccess('inspection')) {
                    navigate('/vehicle-inspection', {
                      state: { openInspectionId: notification.inspectionId },
                    });
                  }
                }}
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {notification.title || 'Admin Update'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {notification.message || 'Task status update'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {notification.createdAt?.toDate
                        ? new Date(notification.createdAt.toDate()).toLocaleString()
                        : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Mark as read">
                      <IconButton
                        size="small"
                        onClick={async (event) => {
                          event.stopPropagation();
                          await markAdminNotificationAsRead(notification.id);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Dismiss">
                      <IconButton
                        size="small"
                        onClick={async (event) => {
                          event.stopPropagation();
                          await markAdminNotificationAsRead(notification.id);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </MenuItem>
            ))}

            <Divider />
            {hasAccess('drivers') && (
              <MenuItem
                onClick={() => {
                  setAnchorNotif(null);
                  navigate('/drivers');
                }}
              >
                View all drivers
              </MenuItem>
            )}
            {hasAccess('inspection') && (
              <MenuItem
                onClick={() => {
                  setAnchorNotif(null);
                  navigate('/vehicle-inspection');
                }}
              >
                View all inspections
              </MenuItem>
            )}
          </Menu>

          <Snackbar
            open={profileSnackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseProfileSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              severity={profileSnackbar.severity}
              onClose={handleCloseProfileSnackbar}
              sx={{ width: '100%' }}
            >
              {profileSnackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
